import InstallCardora from "../InstallCardora";
import "../MobileShell.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useEffect, useState } from "react";
import { Container, Form, Nav, Navbar, NavDropdown, Offcanvas, Spinner } from "react-bootstrap";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import api, { mediaUrl } from "../../../api/axios";
import { isAuth0Enabled } from "../../../auth/auth0Config";

function NotificationLink({ unreadCount = 0 }) {
  return (
    <NavDropdown.Item as={NavLink} to="/notifications" className="navbar-notification-link">
      <span>Notificaciones</span>
      {unreadCount > 0 && <span className="navbar-unread-badge">{unreadCount > 9 ? "9+" : unreadCount}</span>}
    </NavDropdown.Item>
  );
}

const searchStatusLabels = {
  showcase: "En coleccion",
  favorite: "Favorita",
  for_trade: "Disponible",
  for_sale: "Disponible",
  wanted: "Buscada"
};

function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ cards: [], posts: [], users: [], businesses: [] });
  const trimmedQuery = query.trim();
  const showResults = isFocused && trimmedQuery.length >= 2;

  useEffect(() => {
    if (trimmedQuery.length < 2) {
      setResults({ cards: [], posts: [], users: [], businesses: [] });
      setLoading(false);
      return undefined;
    }

    const timeout = setTimeout(() => {
      setLoading(true);
      api.get("/search", { params: { q: trimmedQuery } })
        .then(({ data }) => setResults(data || { cards: [], posts: [], users: [], businesses: [] }))
        .catch(() => setResults({ cards: [], posts: [], users: [], businesses: [] }))
        .finally(() => setLoading(false));
    }, 220);

    return () => clearTimeout(timeout);
  }, [trimmedQuery]);

  const closeSearch = () => setTimeout(() => setIsFocused(false), 140);
  const clearSearch = () => {
    setQuery("");
    setIsFocused(false);
  };

  const openResult = path => {
    clearSearch();
    navigate(path);
  };

  const cardResultPath = card => {
    const profileKey = card.handle || card.user_id;
    const tab = card.status === "wanted" ? "wanted" : "binder";
    const collectionParam = tab === "binder" && card.collection_id ? `&collection=${card.collection_id}` : "";
    return `/u/${profileKey}?tab=${tab}${collectionParam}&card=${card.user_card_id}`;
  };
  const keepSearchOpenForClick = event => event.preventDefault();
  return (
    <div className="navbar-global-search">
      <Form.Control
        size="sm"
        value={query}
        onChange={event => setQuery(event.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={closeSearch}
        onKeyDown={event => {
          if (event.key === "Escape") clearSearch();
        }}
        placeholder="Buscar cartas, tiendas, personas..."
        aria-label="Buscar en Cardora"
      />
      {showResults && (
        <div className="navbar-search-panel">
          <div className="navbar-search-head">
            <strong>Busqueda Cardora</strong>
            {loading && <Spinner animation="border" size="sm" />}
          </div>

          {results.cards?.length > 0 && (
            <div className="navbar-search-section">
              <span>En binders</span>
              {results.cards.slice(0, 5).map(card => (
                <button type="button" className="navbar-search-row" onMouseDown={keepSearchOpenForClick} onClick={() => openResult(cardResultPath(card))} key={card.user_card_id}>
                  {card.image_url && <img src={mediaUrl(card.image_url)} alt="" />}
                  <small>
                    <span className="navbar-search-title-line">
                      <strong>{card.card_name}</strong>
                      <span className={`search-status-tag is-${card.status}`}>{searchStatusLabels[card.status] || card.status}</span>
                    </span>
                    <em>{card.has_real_photo ? "Foto real · " : ""}{card.display_name}{(card.card_type || card.energy_type) ? ` · ${[card.card_type, card.energy_type].filter(Boolean).join(" / ")}` : ""}</em>
                  </small>
                </button>
              ))}
              {results.cards.length > 5 && (
                <button type="button" className="navbar-search-more" onMouseDown={keepSearchOpenForClick} onClick={() => openResult(`/search?q=${encodeURIComponent(trimmedQuery)}&type=cards`)}>
                  Mostrar mas resultados de cartas
                </button>
              )}
            </div>
          )}

          {results.businesses?.length > 0 && (
            <div className="navbar-search-section">
              <span>Comunidades</span>
              {results.businesses.slice(0, 4).map(business => (
                <button type="button" className="navbar-search-text-row" onMouseDown={keepSearchOpenForClick} onClick={() => openResult(`/store/${business.slug}`)} key={business.id}>
                  <strong>{business.name}</strong>
                  <em>{business.description || business.slug}</em>
                </button>
              ))}
            </div>
          )}

          {results.users?.length > 0 && (
            <div className="navbar-search-section">
              <span>Coleccionistas</span>
              {results.users.slice(0, 4).map(user => (
                <button type="button" className="navbar-search-text-row" onMouseDown={keepSearchOpenForClick} onClick={() => openResult(`/u/${user.handle || user.id}`)} key={user.id}>
                  <strong>{user.display_name}</strong>
                  <em>{user.handle ? `@${user.handle}` : "Perfil"}</em>
                </button>
              ))}
            </div>
          )}

          {!loading && !results.cards?.length && !results.businesses?.length && !results.users?.length && (
            <div className="navbar-search-empty">Sin resultados por ahora.</div>
          )}
        </div>
      )}
    </div>
  );
}

function LocalAccountLinks({ unreadCount }) {
  const token = localStorage.getItem("token");

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
  };

  if (!token) {
    return (
      <>
        <NavDropdown.Item as={NavLink} to="/login">
          Entrar
        </NavDropdown.Item>
        <NavDropdown.Item as={NavLink} to="/register">
          Crear cuenta
        </NavDropdown.Item>
      </>
    );
  }

  return (
    <>
      <NavDropdown.Item as={NavLink} to="/profile">
        Perfil
      </NavDropdown.Item>
      <NavDropdown.Item as={NavLink} to="/messages">
        Mensajes
      </NavDropdown.Item>
      <NotificationLink unreadCount={unreadCount} />
      <NavDropdown.Divider />
      <NavDropdown.Item as="button" onClick={handleLogout}>
        Salir
      </NavDropdown.Item>
    </>
  );
}

function Auth0AccountLinks({ unreadCount }) {
  const { isAuthenticated, logout } = useAuth0();
  const token = localStorage.getItem("token");
  const isLoggedIn = isAuthenticated || Boolean(token);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (!isLoggedIn) {
    return (
      <NavDropdown.Item as={NavLink} to="/login">
        Login
      </NavDropdown.Item>
    );
  }

  return (
    <>
      <NavDropdown.Item as={NavLink} to="/profile">
        Perfil
      </NavDropdown.Item>
      <NavDropdown.Item as={NavLink} to="/messages">
        Mensajes
      </NavDropdown.Item>
      <NotificationLink unreadCount={unreadCount} />
      <NavDropdown.Divider />
      <NavDropdown.Item as="button" onClick={handleLogout}>
        Salir
      </NavDropdown.Item>
    </>
  );
}

function AccountLinks({ unreadCount }) {
  if (isAuth0Enabled) {
    return <Auth0AccountLinks unreadCount={unreadCount} />;
  }

  return <LocalAccountLinks unreadCount={unreadCount} />;
}

function MainNavbar() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [interestCount, setInterestCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [showMobileMore, setShowMobileMore] = useState(false);
  const location = useLocation();
  const token = localStorage.getItem("token");
  const currentUser = JSON.parse(localStorage.getItem("user") || "null");
  const isSuperAdmin = currentUser?.role === "super_admin";
  const morePaths = ["/notifications", "/messages", "/profile", "/communities", "/opportunities", "/reports", "/trades", "/stores"];
  const isMoreActive = morePaths.some(path => location.pathname.startsWith(path));

  const loadUnreadCount = useCallback(() => {
    if (!localStorage.getItem("token")) {
      setUnreadCount(0);
      setInterestCount(0);
      return;
    }
    Promise.allSettled([api.get("/notifications/unread-count"), api.get("/trades/me")]).then(([notificationsResult, tradesResult]) => {
      setUnreadCount(notificationsResult.status === "fulfilled" ? Number(notificationsResult.value.data?.count || 0) : 0);
      const trades = tradesResult.status === "fulfilled" && Array.isArray(tradesResult.value.data) ? tradesResult.value.data : [];
      setInterestCount(trades.filter(trade => ["pending", "countered"].includes(trade.status)).length);
    });
  }, []);

  useEffect(() => {
    loadUnreadCount();
    const timer = setInterval(loadUnreadCount, 20000);
    return () => clearInterval(timer);
  }, [loadUnreadCount, token]);

  useEffect(() => {
    setExpanded(false);
    setShowMobileMore(false);
  }, [location.pathname, location.search]);

  return (
    <>
    <Navbar
      bg="white"
      expand="lg"
      expanded={expanded}
      onToggle={setExpanded}
      onSelect={() => setExpanded(false)}
      className="main-navbar border-bottom"
    >
      <Container>
        <Navbar.Brand as={Link} to="/" className="cardora-navbar-brand" onClick={() => setExpanded(false)}>
          <img src="/assets/cardora-mark.png" alt="Cardora" />
          <span>Cardora</span>
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="main-navbar" />
        <Navbar.Collapse id="main-navbar">
          <GlobalSearch />
          <Nav className="ms-auto align-items-lg-center gap-lg-2">
            {!token && (
              <Nav.Link as={NavLink} to="/landing">
                Que es Cardora
              </Nav.Link>
            )}
            <Nav.Link as={NavLink} to="/feed">Inicio</Nav.Link>
            <Nav.Link as={NavLink} to="/meetups">Juntadas</Nav.Link>
            {token && (
              <>
                <Nav.Link as={NavLink} to="/binder">
                  Binder
                </Nav.Link>
                {isSuperAdmin && (
                  <Nav.Link as={NavLink} to="/super-admin">
                    Superadmin
                  </Nav.Link>
                )}
              </>
            )}
            <NavDropdown title="Mas" id="explore-nav">
              <NavDropdown.Item as={NavLink} to="/communities">
                Comunidades
              </NavDropdown.Item>
              <NavDropdown.Item as={NavLink} to="/opportunities">
                Oportunidades
              </NavDropdown.Item>
              <NavDropdown.Item as={NavLink} to="/reports">
                Reporte Cardora
              </NavDropdown.Item>
              <NavDropdown.Item as={NavLink} to="/trades" className="navbar-interest-link">
                Intereses {interestCount > 0 && <span className="navbar-unread-badge">{interestCount > 9 ? "9+" : interestCount}</span>}
              </NavDropdown.Item>
            </NavDropdown>
            <NavDropdown title="Tiendas" id="stores-nav">
              <NavDropdown.Item as={NavLink} to="/communities">
                Directorio
              </NavDropdown.Item>
              <NavDropdown.Item as={NavLink} to="/stores/register">
                Registrar tienda
              </NavDropdown.Item>
            </NavDropdown>
            {!token && (
              <Nav.Link as={NavLink} to="/register" className="navbar-register-link">
                Crear cuenta
              </Nav.Link>
            )}
            <NavDropdown
              title={(
                <span className="navbar-account-title">
                  Cuenta
                  {unreadCount > 0 && <span className="navbar-unread-dot" />}
                </span>
              )}
              id="account-nav"
              align="end"
            >
              <AccountLinks unreadCount={unreadCount} />
            </NavDropdown>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
    <nav className="mobile-primary-nav" aria-label="Navegacion principal">
      <NavLink to="/feed"><i className="bi bi-house"/><span>Inicio</span></NavLink>
      <NavLink to="/search"><i className="bi bi-search"/><span>Buscar</span></NavLink>
      {token ? <NavLink to="/binder" className="mobile-primary-action"><i className="bi bi-journal-album"/><span>Binder</span></NavLink> : <NavLink to="/communities"><i className="bi bi-shop"/><span>Comunidades</span></NavLink>}
      <NavLink to="/meetups"><i className="bi bi-people"/><span>Juntadas</span></NavLink>
      <button type="button" className={(isMoreActive ? "active " : "") + (unreadCount ? "has-unread" : "")} onClick={() => setShowMobileMore(current => !current)} aria-expanded={showMobileMore} aria-controls="mobile-more-menu" aria-label="Abrir mas opciones"><i className="bi bi-grid"/><span>Mas</span></button>
    </nav>
    <Offcanvas id="mobile-more-menu" show={showMobileMore} onHide={() => setShowMobileMore(false)} placement="bottom" className="mobile-more-sheet">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title><img src="/assets/cardora-mark.png" alt="" />Cardora</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body onClickCapture={event => { if (event.target.closest("a, button")) setShowMobileMore(false); }}>
        <div className="mobile-more-grid">
          {token && <NavLink to="/notifications" className={unreadCount ? "has-count" : ""}><i className="bi bi-bell"/><span>Avisos</span>{unreadCount > 0 && <b>{unreadCount > 9 ? "9+" : unreadCount}</b>}</NavLink>}
          {token && <NavLink to="/messages"><i className="bi bi-envelope"/><span>Mensajes</span></NavLink>}
          {token && <NavLink to="/profile"><i className="bi bi-person"/><span>Mi perfil</span></NavLink>}
          {token && <NavLink to="/profile?tab=badges"><i className="bi bi-award"/><span>Medallas</span></NavLink>}
          <NavLink to="/communities"><i className="bi bi-shop"/><span>Comunidades</span></NavLink>
          <NavLink to="/opportunities"><i className="bi bi-stars"/><span>Oportunidades</span></NavLink>
          <NavLink to="/trades" className={interestCount ? "has-count" : ""}><i className="bi bi-arrow-left-right"/><span>Intereses</span>{interestCount > 0 && <b>{interestCount > 9 ? "9+" : interestCount}</b>}</NavLink>
          <NavLink to="/reports"><i className="bi bi-bar-chart"/><span>Reporte</span></NavLink>
          <NavLink to="/stores/register"><i className="bi bi-building-add"/><span>Registrar tienda</span></NavLink>
          {!token && <NavLink to="/login"><i className="bi bi-box-arrow-in-right"/><span>Entrar</span></NavLink>}
          {!token && <NavLink to="/register"><i className="bi bi-person-plus"/><span>Crear cuenta</span></NavLink>}
          {isSuperAdmin && <NavLink to="/super-admin"><i className="bi bi-shield-check"/><span>Superadmin</span></NavLink>}
        </div>
        <InstallCardora />
        {token && <div className="mobile-more-account"><AccountLinks unreadCount={unreadCount} /></div>}
      </Offcanvas.Body>
    </Offcanvas>
    </>
  );
}

export default MainNavbar;
